import { type ColumnDef } from "@tanstack/react-table";
import type { ApiDilemma } from "../types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowUpDown,
  CalendarClock,
  CircleEllipsis,
  Copy,
  Languages,
  PenBox,
  Send,
  Undo2,
  Archive,
  ArchiveRestore,
  History,
  Sparkle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { TFunction } from "i18next";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/utils/date";
import { NavLink, useNavigate } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import DeleteButton from "@/components/delete-button";
import { dilemmasRepo } from "@/lib/repositories";
import { toast } from "sonner";
import { handleApiError } from "@/utils/errors";
import { catchError } from "@/lib/errors";
import { queryClient } from "@/lib/query-client";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import DilemmaScheduler from "./dilemma-scheduler";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { statusBadgeVariant, isDilemmaPublishable } from "../utils";
import { DILEMMAS_STATUS } from "../constants";
import SuccessRateCell from "@/components/success-rate-cell";
import SuccessRateSortableHeader from "@/components/success-rate-sortable-header";
import { useDilemmasFilterStore } from "../stores";

async function onDelete(id: string, t: TFunction) {
  if (!id) return;
  const [error] = await catchError(dilemmasRepo.delete(id));
  if (error) {
    toast.error(handleApiError(error).message);
    return;
  }
  await queryClient.invalidateQueries({ queryKey: ["dilemmas"] });
  toast.success(t("messages.dilemma_delete_success"));
}

async function onPublish(id: string, t: TFunction) {
  const [error] = await catchError(
    dilemmasRepo.patch(id, { status: DILEMMAS_STATUS.PUBLISHED }),
  );
  if (error) {
    toast.error(handleApiError(error).message);
    return;
  }
  await queryClient.invalidateQueries({ queryKey: ["dilemmas"] });
  toast.success(t("messages.dilemma_publish_success"));
}

async function onUnpublish(id: string, t: TFunction) {
  const [error] = await catchError(
    dilemmasRepo.patch(id, { status: DILEMMAS_STATUS.DRAFT }),
  );
  if (error) {
    toast.error(handleApiError(error).message);
    return;
  }
  await queryClient.invalidateQueries({ queryKey: ["dilemmas"] });
  toast.success(t("messages.dilemma_unpublish_success"));
}

async function onArchive(id: string, t: TFunction) {
  const [error] = await catchError(
    dilemmasRepo.patch(id, { status: DILEMMAS_STATUS.ARCHIVED }),
  );
  if (error) {
    toast.error(handleApiError(error).message);
    return;
  }
  await queryClient.invalidateQueries({ queryKey: ["dilemmas"] });
  toast.success(t("messages.dilemma_archive_success"));
}

async function onUnarchive(id: string, t: TFunction) {
  const [error] = await catchError(
    dilemmasRepo.patch(id, { status: DILEMMAS_STATUS.PUBLISHED }),
  );
  if (error) {
    toast.error(handleApiError(error).message);
    return;
  }
  await queryClient.invalidateQueries({ queryKey: ["dilemmas"] });
  toast.success(t("messages.dilemma_unarchive_success"));
}

async function onSchedule(id: string, scheduledDate: string, t: TFunction) {
  const [error] = await catchError(
    dilemmasRepo.patch(id, {
      status: DILEMMAS_STATUS.SCHEDULED,
      scheduled_for_date: scheduledDate,
    }),
  );
  if (error) {
    toast.error(handleApiError(error).message);
    return;
  }
  await queryClient.invalidateQueries({ queryKey: ["dilemmas"] });
  toast.success(t("messages.dilemma_schedule_success"));
}

function statusLabel(
  status: string,
  t: TFunction,
  context?: "submissions",
): string {
  if (context === "submissions" && status === DILEMMAS_STATUS.DRAFT) {
    return t("modules.dilemmas.keys.status_returned");
  }
  switch (status) {
    case DILEMMAS_STATUS.DRAFT:
      return t("modules.dilemmas.keys.status_draft");
    case DILEMMAS_STATUS.SCHEDULED:
      return t("modules.dilemmas.keys.status_scheduled");
    case DILEMMAS_STATUS.PUBLISHED:
      return t("modules.dilemmas.keys.status_published");
    case DILEMMAS_STATUS.ARCHIVED:
      return t("modules.dilemmas.keys.status_archived");
    case DILEMMAS_STATUS.SUBMITTED:
      return t("modules.dilemmas.keys.status_submitted");
    default:
      return status;
  }
}

export const submissionsColumns = (t: TFunction): ColumnDef<ApiDilemma>[] => [
  {
    accessorKey: "reference",
    header: t("keys.id_reference"),
    cell: ({ row }) => {
      return (
        <NavLink to={ROUTES.SUBMISSION_DETAILS(row.original.id)} end>
          <span className="text-gray-400 hover:text-primary hover:underline">
            {row.getValue("reference")}
          </span>
        </NavLink>
      );
    },
  },
  {
    accessorKey: "name",
    header: t("modules.dilemmas.keys.name"),
    cell: ({ row }) => {
      return (
        <span className="font-medium truncate inline-block max-w-56">
          {row.getValue("name")}
        </span>
      );
    },
  },
  {
    accessorKey: "author",
    header: t("modules.dilemmas.keys.author"),
    cell: ({ row }) => {
      const author = row.original.author;
      return (
        <span className="font-medium truncate inline-block max-w-56">
          {author?.first_name} {author?.last_name}
        </span>
      );
    },
  },
  {
    id: "topic",
    header: t("modules.dilemmas.keys.theme"),
    cell: ({ row }) => {
      const topic = row.original.topic;
      return (
        <span
          className="text-sm truncate inline-block max-w-56"
          style={{ color: topic.color }}
        >
          {topic.name}
        </span>
      );
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <button
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="flex items-center gap-1 cursor-pointer"
      >
        {t("modules.dilemmas.keys.status")}
        <ArrowUpDown
          className={`ml-1 size-4 ${column.getIsSorted() ? "text-primary" : "opacity-50"}`}
        />
      </button>
    ),
    cell: ({ row }) => {
      const status = row.getValue("status") as string;
      return (
        <Badge variant={statusBadgeVariant(status)}>
          {statusLabel(status, t, "submissions")}
        </Badge>
      );
    },
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => (
      <button
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="flex items-center gap-1 cursor-pointer"
      >
        {t("keys.creation_date")}
        <ArrowUpDown
          className={`ml-1 size-4 ${column.getIsSorted() ? "text-primary" : "opacity-50"}`}
        />
      </button>
    ),
    cell: ({ row }) => (
      <span className="text-gray-500">
        {formatDate(row.getValue("created_at"))}
      </span>
    ),
  },
  {
    id: "actions",
    header: t("keys.table.actions"),
    cell: ({ row }) => {
      const dilemma = row.original;
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <span className="sr-only">Ouvrir le menu</span>
              <CircleEllipsis className="h-10 w-10 rotate-90 text-gray-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <NavLink to={ROUTES.SUBMISSION_DETAILS(dilemma.id)} end>
                <PenBox />
                {t("keys.table.edit")}
              </NavLink>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <NavLink
                to={`${ROUTES.HISTORY}?object_pk=${dilemma.id}&model_type=dilemma`}
                end
              >
                <History />
                {t("keys.table.view_history")}
              </NavLink>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];

// eslint-disable-next-line react-refresh/only-export-components
function DilemmaActionsCell({
  dilemma,
  t,
}: {
  dilemma: ApiDilemma;
  t: TFunction;
}) {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [schedulerOpen, setSchedulerOpen] = useState(false);
  const [isScheduleLoading, setIsScheduleLoading] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const isPublished = dilemma.status === DILEMMAS_STATUS.PUBLISHED;
  const isArchived = dilemma.status === DILEMMAS_STATUS.ARCHIVED;
  const canSchedule =
    dilemma.status === DILEMMAS_STATUS.DRAFT ||
    dilemma.status === DILEMMAS_STATUS.SCHEDULED;

  const handleSchedule = async (scheduledDate: string) => {
    setIsScheduleLoading(true);
    await onSchedule(dilemma.id, scheduledDate, t);
    setIsScheduleLoading(false);
  };

  const handleDuplicate = async () => {
    setIsDuplicating(true);
    const [error, copy] = await catchError(dilemmasRepo.duplicate(dilemma.id));
    setIsDuplicating(false);
    if (error) {
      const apiError = handleApiError(error);
      if (apiError.status === 404) {
        toast.error(t("messages.dilemma_not_found"));
        await queryClient.invalidateQueries({ queryKey: ["dilemmas"] });
        return;
      }
      toast.error(apiError.message);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["dilemmas"] });
    toast.success(t("messages.dilemma_duplicate_success"));
    navigate(ROUTES.DILEMMA_DETAILS(copy.id));
  };

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <span className="sr-only">Ouvrir le menu</span>
            <CircleEllipsis className="h-10 w-10 rotate-90 text-gray-400" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <NavLink to={ROUTES.DILEMMA_DETAILS(dilemma.id)} end>
              <PenBox />
              {t("keys.table.edit")}
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <NavLink to={ROUTES.DILEMMA_TRANSLATE(dilemma.id)} end>
              <Languages />
              {t("modules.dilemmas.keys.edit_translation")}
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => e.preventDefault()}
            onClick={handleDuplicate}
            disabled={isDuplicating}
          >
            <Copy />
            {t("modules.dilemmas.keys.duplicate")}
          </DropdownMenuItem>
          {!isPublished &&
            !isArchived &&
            (isDilemmaPublishable(dilemma) ? (
              <DropdownMenuItem onClick={() => onPublish(dilemma.id, t)}>
                <Send />
                {t("modules.dilemmas.keys.publish")}
              </DropdownMenuItem>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <DropdownMenuItem disabled>
                        <Send />
                        {t("modules.dilemmas.keys.publish")}
                      </DropdownMenuItem>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("modules.dilemmas.errors.incomplete_dilemma")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          {isPublished && (
            <DropdownMenuItem onClick={() => onUnpublish(dilemma.id, t)}>
              <Undo2 />
              {t("modules.dilemmas.keys.unpublish")}
            </DropdownMenuItem>
          )}
          {canSchedule && (
            <DropdownMenuItem
              onClick={() => {
                setDropdownOpen(false);
                setSchedulerOpen(true);
              }}
            >
              <CalendarClock />
              {t("keys.schedule_publication")}
            </DropdownMenuItem>
          )}
          {!isArchived && (
            <DropdownMenuItem onClick={() => onArchive(dilemma.id, t)}>
              <Archive />
              {t("modules.dilemmas.keys.archive")}
            </DropdownMenuItem>
          )}
          {isArchived && (
            <DropdownMenuItem onClick={() => onUnarchive(dilemma.id, t)}>
              <ArchiveRestore />
              {t("modules.dilemmas.keys.unarchive")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild>
            <NavLink
              to={`${ROUTES.HISTORY}?object_pk=${dilemma.id}&model_type=dilemma`}
              end
            >
              <History />
              {t("keys.table.view_history")}
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <DeleteButton
              onClick={() => onDelete(dilemma.id, t)}
              className="w-full justify-start"
            />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {canSchedule && (
        <DilemmaScheduler
          open={schedulerOpen}
          onOpenChange={setSchedulerOpen}
          isLoading={isScheduleLoading}
          onConfirm={handleSchedule}
          initialDate={dilemma.scheduled_for_date}
          disabled={false}
        />
      )}
    </>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
function DilemmaSuccessRateHeader({ t }: { t: TFunction }) {
  const ordering = useDilemmasFilterStore((s) => s.ordering);
  const setOrdering = useDilemmasFilterStore((s) => s.setOrdering);
  return (
    <SuccessRateSortableHeader
      ordering={ordering}
      onOrderingChange={setOrdering}
      label={t("keys.success_rate")}
    />
  );
}

export const dilemmasColumns = (t: TFunction): ColumnDef<ApiDilemma>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label={t("keys.select_all")}
      />
    ),
    cell: ({ row }) => {
      const dilemma = row.original;
      const isIncomplete =
        dilemma.status === DILEMMAS_STATUS.DRAFT &&
        !isDilemmaPublishable(dilemma);
      const checkbox = (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label={t("keys.select_row")}
        />
      );
      if (!isIncomplete) return checkbox;
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>{checkbox}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("modules.dilemmas.errors.incomplete_dilemma")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    },
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "reference",
    header: t("keys.id_reference"),
    cell: ({ row }) => {
      return (
        <NavLink to={ROUTES.DILEMMA_DETAILS(row.original.id)} end>
          <span className="text-gray-400 hover:text-primary hover:underline">
            {row.getValue("reference")}
          </span>
        </NavLink>
      );
    },
  },
  {
    accessorKey: "name",
    header: t("modules.dilemmas.keys.name"),
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-1">
          {row.original.is_ai_generated && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Sparkle size={14} className="text-primary" />
                </TooltipTrigger>
                <TooltipContent>
                  {t("modules.dilemmas.ai_generated")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <span className="font-medium truncate inline-block max-w-56">
            {row.getValue("name")}
          </span>
        </div>
      );
    },
  },
  {
    id: "topic",
    header: t("modules.dilemmas.keys.theme"),
    cell: ({ row }) => {
      const topic = row.original.topic;
      return (
        <span
          className="text-sm truncate inline-block max-w-56"
          style={{ color: topic.color }}
        >
          {topic.name}
        </span>
      );
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <button
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="flex items-center gap-1 cursor-pointer"
      >
        {t("modules.dilemmas.keys.status")}
        <ArrowUpDown
          className={`ml-1 size-4 ${column.getIsSorted() ? "text-primary" : "opacity-50"}`}
        />
      </button>
    ),
    cell: ({ row }) => {
      const status = row.getValue("status") as string;
      return (
        <Badge variant={statusBadgeVariant(status)}>
          {statusLabel(status, t)}
        </Badge>
      );
    },
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => (
      <button
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="flex items-center gap-1 cursor-pointer"
      >
        {t("keys.creation_date")}
        <ArrowUpDown
          className={`ml-1 size-4 ${column.getIsSorted() ? "text-primary" : "opacity-50"}`}
        />
      </button>
    ),
    cell: ({ row }) => (
      <span className="text-gray-500">
        {formatDate(row.getValue("created_at"))}
      </span>
    ),
  },
  {
    accessorKey: "published_at",
    header: ({ column }) => (
      <button
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="flex items-center gap-1 cursor-pointer"
      >
        {t("modules.dilemmas.keys.published_at")}
        <ArrowUpDown
          className={`ml-1 size-4 ${column.getIsSorted() ? "text-primary" : "opacity-50"}`}
        />
      </button>
    ),
    cell: ({ row }) => {
      const value = row.getValue("published_at") as string | null;
      return (
        <span className="text-gray-500">{value ? formatDate(value) : "-"}</span>
      );
    },
  },
  {
    id: "success_rate",
    enableSorting: false,
    header: () => <DilemmaSuccessRateHeader t={t} />,
    cell: ({ row }) => <SuccessRateCell value={row.original.success_rate} />,
  },
  {
    id: "actions",
    header: t("keys.table.actions"),
    cell: ({ row }) => <DilemmaActionsCell dilemma={row.original} t={t} />,
  },
];
